// TUT-465 — o último botão do trilho: o JEITO do menu, salvo por pessoa no
// perfil (`POST /auth/menu`), como o tema.
//
// TUT-467 — o dono reprovou o popover "Jeito do menu" (03/10: "o escrito jeito
// do menu pra configurar o menu também está muito ruim") e mostrou o outro app
// da casa (BreviTI) como referência: o último botão é UM botão, com o NOME do
// jeito atual ("Menu expandido", "Menu recolhido"), e cada clique passa para o
// outro e salva. Sem lista, sem rádio, sem palavra de configuração.
//
// TUT-521 — o dono tirou o "Menu híbrido" (23/10): o botão só alterna entre
// os dois jeitos que sobraram.
import { PanelLeft, PanelLeftClose } from "lucide-react";
import type { ComponentType } from "react";
import { useSidebar } from "@/components/ui/sidebar";
import { api } from "@/lib/api";
import { adoptMenuPreference } from "@/lib/useMenuPreference";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { MenuPreference } from "@/lib/types";

type Jeito = {
  nome: string;
  icone: ComponentType<{ "aria-hidden"?: boolean | "true"; className?: string }>;
};

export const JEITOS_DO_MENU: Record<MenuPreference, Jeito> = {
  texto: { nome: "Menu expandido", icone: PanelLeft },
  icones: { nome: "Menu recolhido", icone: PanelLeftClose },
};

/**
 * TUT-465 — a tela muda na hora; gravar no perfil é fire-and-forget. TUT-547 —
 * mora aqui, e não no AppShell, porque o portal externo usa o mesmo menu.
 */
export function escolherJeito(proximo: MenuPreference) {
  adoptMenuPreference(proximo);
  void api.setMenuPreference(proximo).catch(() => {
    // Sem rede, a escolha fica só neste navegador até a próxima gravação.
  });
}

/** O clique alterna: expandido ↔ recolhido. */
export function proximoJeitoDoMenu(atual: MenuPreference): MenuPreference {
  return atual === "texto" ? "icones" : "texto";
}

/**
 * O botão segue a anatomia do item do menu (mesmo piso, mesmo vão, mesmo
 * ícone), e o contorno de acento no hover e no foco é o da referência.
 */
const botaoClass =
  "flex min-h-[2.92rem] w-full min-w-0 items-center gap-[0.9rem] rounded-[var(--radius-bloco)] border border-transparent px-[0.85rem] text-left text-destaque font-semibold text-[var(--sidebar-muted)] outline-none transition-colors motion-reduce:transition-none " +
  "hover:border-[var(--accent)] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:border-[var(--accent)] focus-visible:ring-2 focus-visible:ring-sidebar-ring " +
  "group-data-[collapsible=icon]:min-h-0 group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:rounded-full group-data-[collapsible=icon]:p-2";

export function EscolhaDoJeitoDoMenu({
  jeito,
  aoEscolher,
}: {
  jeito: MenuPreference;
  aoEscolher: (proximo: MenuPreference) => void;
}) {
  const { isMobile, state } = useSidebar();
  // No celular o menu é a gaveta e a barra de baixo: o jeito do trilho não se
  // aplica, e o botão some.
  if (isMobile) return null;
  const { nome, icone: Icone } = JEITOS_DO_MENU[jeito];
  const proximo = JEITOS_DO_MENU[proximoJeitoDoMenu(jeito)].nome;
  return (
    <div data-escolha-do-menu="" className="mt-auto p-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            // O nome visível ("Menu recolhido") abre o nome acessível, e o resto
            // diz o que o clique faz (WCAG 2.5.3, rótulo no nome).
            aria-label={`${nome}. Trocar para ${proximo.toLowerCase()}`}
            data-jeito-do-menu={jeito}
            className={botaoClass}
            onClick={() => aoEscolher(proximoJeitoDoMenu(jeito))}
          >
            <Icone aria-hidden="true" className="flex-none" />
            <span className="truncate group-data-[collapsible=icon]:hidden">{nome}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" align="center" hidden={state !== "collapsed"}>
          {nome} — clique para {proximo.toLowerCase()}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
